/**
 * OneSignal web SDK bootstrap — dormant by default.
 *
 * Meetball is migrating push from self-managed VAPID web push + native FCM to
 * OneSignal WITHOUT losing existing subscribers. This module lands the corrected
 * foundation (superseding PR #780, which merged OneSignal's SW into the root
 * Workbox SW via unguarded CDN importScripts — a single-registration conflict
 * with the legacy VAPID subscription and an SW-install fragility risk).
 *
 * Design:
 *   - OneSignal runs its OWN service worker on its OWN scope (/push/onesignal/),
 *     registered from public/push/onesignal/OneSignalSDKWorker.js. It never
 *     touches src/service-worker.ts (the root Workbox SW), so a CDN failure in
 *     OneSignal's worker only breaks OneSignal, never the app shell / offline /
 *     legacy push.
 *   - Everything stays DORMANT until VITE_ONESIGNAL_APP_ID is set per
 *     environment (unset in every env today → zero behaviour change).
 *
 * Contracts (later tasks — ownership choreography, notification events — rely on
 * exactly these):
 *   - initOneSignal(): idempotent, no-op on native / missing appId, NEVER
 *     rejects (fire-and-forget callers must not unhandled-reject).
 *   - whenOneSignalReady(): REJECTS if init failed or never ran (missing
 *     appId / native). Callers MUST catch. This is a deliberate contract change
 *     vs #780: it does not silently resolve, so "not configured" can't be
 *     mistaken for "ready".
 *   - isOneSignalConfigured() / isOneSignalPushOwner(): environment gates.
 */
import OneSignal from 'react-onesignal';
import { isNativePlatform } from '@lib/platform';
import { logger } from '@utils/logger';
import { sanitizeError } from '@utils/sanitizeError';
// Module-scope (non-React) analytics: emit via amplitude.track directly, reusing
// the mechanism src/lib/webVitals.ts uses. The Amplitude SDK buffers events
// fired before init in its own dispatch queue, so a direct call is safe even if
// Amplitude has not finished initialising. Do NOT invent a new pipeline, and do
// NOT reach for the useAmplitudeTracking hook (unavailable outside React).
import { amplitude } from '@lib/amplitude';
import { ANALYTICS_EVENTS } from '@lib/analytics-events';

/**
 * True only on the web platform with a non-empty VITE_ONESIGNAL_APP_ID.
 * Native (Capacitor) push is handled by the existing FCM pipeline, not the web
 * SDK, so OneSignal-web is always disabled there.
 */
export function isOneSignalConfigured(): boolean {
  return !isNativePlatform() && Boolean(import.meta.env.VITE_ONESIGNAL_APP_ID);
}

/**
 * True when OneSignal is configured AND this deployment is the designated push
 * owner (VITE_ONESIGNAL_PUSH_OWNER === 'true'). The ownership flag lets a later
 * task hand push delivery from the legacy VAPID/FCM pipeline to OneSignal one
 * environment at a time without a flag-day cutover.
 */
export function isOneSignalPushOwner(): boolean {
  return isOneSignalConfigured() && import.meta.env.VITE_ONESIGNAL_PUSH_OWNER === 'true';
}

/**
 * Singleton for the in-flight / settled init. `whenOneSignalReady()` hands back
 * the REAL promise (which may reject); `initOneSignal()` hands back a swallowed
 * copy so fire-and-forget callers never unhandled-reject. Reset to null on
 * failure so a later init attempt (from either entry point) can retry.
 */
let initPromise: Promise<void> | null = null;

/**
 * How many real OneSignal.init attempts have been started this session. A
 * transient CDN blip or SW-registration race at startup would otherwise be
 * permanent for the whole session (nothing calls init again), so readiness
 * waiters may kick a fresh attempt — bounded here so a persistently broken
 * OneSignal can't spin init forever.
 */
let initAttempts = 0;
const MAX_INIT_ATTEMPTS_PER_SESSION = 3;

/**
 * Start ONE real OneSignal.init attempt, wiring the shared singleton + failure
 * telemetry. Assigns `initPromise` to the REAL (possibly-rejecting) promise and
 * returns it. On failure it logs, emits onesignal_init_failed, and nulls
 * `initPromise` so a later attempt can retry (up to the per-session cap). Callers
 * that must not reject wrap the result in `.catch(() => undefined)`.
 *
 * Precondition: OneSignal is configured and `appId` is set (the entry points
 * check this and the attempt cap before calling).
 */
function startInitAttempt(appId: string): Promise<void> {
  initAttempts += 1;
  initPromise = OneSignal.init({
    appId,
    // Localhost counts as a secure origin only in dev, so the SDK can register
    // its worker over plain http://localhost during local development.
    allowLocalhostAsSecureOrigin: import.meta.env.DEV,
    // Re-subscribe returning permission-granted users automatically — but ONLY
    // when this deployment is the push owner. In the shadow phase (APP_ID set,
    // owner flag not yet flipped) the legacy VAPID pipeline is still live and
    // useLegacyWebPushTeardown is NOT mounted, so auto-opting devices into
    // OneSignal here would leave them reachable by BOTH pipelines (duplicate
    // notifications) with nothing to close the legacy path. Gating on the owner
    // flag keeps the shadow phase observe-only and makes the documented rollout
    // order (owner flag before the backend send flag) safe even if the backend
    // flag is flipped first.
    autoResubscribe: isOneSignalPushOwner(),
    // Click behaviour when a tab is already open: focus the existing
    // same-origin tab and leave navigation to the page-side `click` listener
    // (useOneSignalNotificationEvents' SPA navigate). Without these the SDK
    // defaults to match 'exact' + action 'navigate' — and because the
    // backend's launch `url` is ABSOLUTE and the web `click` event has no
    // preventDefault, a tap while the app sits on any other route opens a NEW
    // tab / full document load instead of the legacy soft nav. Cold start
    // (no tab open) still opens the launch URL. These options are
    // historically under-documented in the SDK (OneSignal-website-sdk#337),
    // so Gate 2 of the rollout runbook device-verifies focus-not-new-tab
    // explicitly.
    notificationClickHandlerMatch: 'origin',
    notificationClickHandlerAction: 'focus',
    // Scoped worker: served from public/push/onesignal/ and registered at
    // /push/onesignal/ so it never collides with the root Workbox SW at '/'.
    // serviceWorkerPath MUST be relative to the site root with NO leading slash
    // ("push/onesignal/…", not "/push/onesignal/…"). The OneSignal v16 SDK
    // resolves this against the ORIGIN root (not the current document), so a
    // leading slash is a documented format error: the SDK discards the custom
    // path and falls back to registering "/OneSignalSDKWorker.js" at the root,
    // which 404s (our file lives under /push/onesignal/) and init fails. Scope
    // stays absolute — it IS a URL path.
    serviceWorkerPath: 'push/onesignal/OneSignalSDKWorker.js',
    serviceWorkerParam: { scope: '/push/onesignal/' },
  })
    .then(() => {
      logger.info('OneSignal: initialised');
    })
    .catch((error: unknown) => {
      logger.error('OneSignal: init failed', sanitizeError(error));
      try {
        amplitude.track(ANALYTICS_EVENTS.onesignal_init_failed, sanitizeError(error));
      } catch {
        // Telemetry must never mask the original init failure.
      }
      // Allow a later attempt (initOneSignal() OR a readiness waiter) to retry,
      // still bounded by the per-session cap via initAttempts.
      initPromise = null;
      // Re-throw so whenOneSignalReady() consumers observe the failure.
      throw error;
    });
  return initPromise;
}

/**
 * Initialise the OneSignal web SDK. Idempotent, dormant when unconfigured, and
 * guaranteed not to reject. Safe to call fire-and-forget at app startup.
 */
export function initOneSignal(): Promise<void> {
  // Dormant no-op. Note whenOneSignalReady() still rejects in this state — that
  // asymmetry is the point: callers awaiting readiness learn OneSignal is not
  // available rather than hanging or falsely resolving.
  const appId = import.meta.env.VITE_ONESIGNAL_APP_ID;
  if (!isOneSignalConfigured() || !appId) return Promise.resolve();

  // Already initialising or initialised: return the swallowed view so a repeat
  // caller neither re-inits nor risks an unhandled rejection.
  if (initPromise) return initPromise.catch(() => undefined);

  // A previous attempt failed and reset initPromise. Retry only while under the
  // per-session cap; once exhausted, no-op (still never rejects).
  if (initAttempts >= MAX_INIT_ATTEMPTS_PER_SESSION) return Promise.resolve();

  // Fire-and-forget callers get the swallowed promise; readiness consumers call
  // whenOneSignalReady() for the real (possibly-rejecting) one.
  return startInitAttempt(appId).catch(() => undefined);
}

/**
 * Resolves once OneSignal has initialised; REJECTS if init failed or never ran
 * (dormant: missing appId or native platform). Callers MUST catch.
 *
 * If a prior init attempt failed (initPromise reset to null) and OneSignal is
 * configured, a readiness waiter transparently kicks a fresh real init attempt —
 * bounded by MAX_INIT_ATTEMPTS_PER_SESSION — so a startup blip doesn't leave
 * identity / opt-in / events / teardown dead for the rest of the session. Once
 * the cap is exhausted (or when unconfigured) it rejects as before.
 */
export function whenOneSignalReady(): Promise<void> {
  // An init is in flight or already settled — hand back the real promise.
  if (initPromise) return initPromise;

  // No init pending: retry lazily if configured and under the cap, otherwise
  // reject (dormant build, or attempts exhausted).
  const appId = import.meta.env.VITE_ONESIGNAL_APP_ID;
  if (isOneSignalConfigured() && appId && initAttempts < MAX_INIT_ATTEMPTS_PER_SESSION) {
    return startInitAttempt(appId);
  }
  return Promise.reject(new Error('OneSignal not initialised'));
}
